import EnlargedImageRenderProp from '@src/components/common/EnlargedImageRenderProp';
import EnlargedPreviewCard, {
  fitPreviewToAspectRatio,
  viewportMaxPreviewHeight,
} from '@src/components/common/EnlargedPreviewCard';
import Switch from '@src/components/common/Switch';
import { mapToElementState } from '@src/renderer/ElementState';
import { isUploadedMediaCardId } from '@src/services/videoTemplate/uploadedMediaCard';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import GearIcon from '@src/svgs/GearIcon';
import parseAspectRatio from '@src/utility/parseAspectRatio';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import { COMPACT_TOOLTIP_STYLE } from '../../common/tooltipStyles';
import { useCompositionContext } from '../CompositionContext';
import { CaptionsPreviewThumbnail } from './CaptionsPreviewThumbnail';
import { ReplaceCaptionsModal } from './ReplaceCaptionsModal';
import { ReplaceElementModal } from './ReplaceCompositionModal';
import {
  type ElementOption,
  UpdateTemplatePlacementModal,
} from './UpdateTemplatePlacementModal';
import { useTemplateElementToggleQueue } from './useTemplateElementToggleQueue';
import {
  useVideoTemplatesContext,
  type VideoTemplateElementType,
} from './VideoTemplatesContext';

import type { VideoTemplateConfig } from './types';
import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';

/** Every template element previews into the same box, whatever its shape. */
const ELEMENT_PREVIEW_MAX_HEIGHT_PX = 200;

function getConfigEnabled(
  type: VideoTemplateElementType,
  config: VideoTemplateConfig | null | undefined,
): boolean | undefined {
  if (!config) return undefined;
  switch (type) {
    case 'logo':
      return config.logoConfiguration?.enabled;
    case 'captions':
      return config.captionsConfiguration?.enabled;
    case 'introCard':
      return config.introCardConfiguration?.enabled;
    case 'endCard':
      return config.endCardConfiguration?.enabled;
    case 'nameCard':
      return config.namecardConfiguration?.enabled;
    case 'hook':
      return config.hookConfiguration?.enabled;
    default:
      return undefined;
  }
}

function getConfigLockPosition(
  type: VideoTemplateElementType,
  config: VideoTemplateConfig | null | undefined,
): boolean {
  if (!config) return false;
  switch (type) {
    case 'logo':
      return config.logoConfiguration?.lockPosition ?? false;
    case 'captions':
      return config.captionsConfiguration?.lockPosition ?? false;
    case 'introCard':
      return config.introCardConfiguration?.lockPosition ?? false;
    case 'endCard':
      return config.endCardConfiguration?.lockPosition ?? false;
    case 'nameCard':
      return config.namecardConfiguration?.lockPosition ?? false;
    case 'hook':
      return config.hookConfiguration?.lockPosition ?? false;
    default:
      return false;
  }
}

interface VideoTemplateSettingsProps {
  videoCreator: IVideoCreatorStore;
  compact?: boolean;
  disabled?: boolean;
  editor: IVideoEditor;
  /** Externally controlled placement modal state. When provided, hides the internal gear button. */
  isPlacementModalOpen?: boolean;
  onClosePlacementModal?: () => void;
}

export const VideoTemplateSettings = observer(
  ({
    videoCreator,
    compact,
    disabled,
    isPlacementModalOpen: externalModalOpen,
    onClosePlacementModal,
    editor,
  }: VideoTemplateSettingsProps) => {
    const {
      activeTemplate,
      activeTemplateSettings,
      replaceTemplateElement,
      speakers,
      getReplacedElementId,
      isClipMode,
      updateTemplateConfig,
      getPlacementDeviation,
      hasAccessToEditAll,
      hasAccessToEditThisTemplate,
      saveTemplateAsCopy,
      syncKaraokeBrandingTemplates,
    } = useVideoTemplatesContext();
    const { queueToggle, isEnabledOptimistic } =
      useTemplateElementToggleQueue();
    const { getCompositionById } = useCompositionContext();

    const userIdentity = useUserIdentity();
    const { enableTemplateLockPosition } = useFlagsCombination(
      userIdentity.currentRole,
    );
    // Only admins (with the feature flag on) may adjust the per-element lock in
    // the app. Enforcement of an already-set lock is independent of this gate.
    const canEditLockPosition =
      !!enableTemplateLockPosition &&
      (hasAccessToEditAll || hasAccessToEditThisTemplate);

    const [internalModalOpen, setInternalModalOpen] = useState(false);
    const isExternallyControlled = externalModalOpen !== undefined;
    const isPlacementModalOpen = isExternallyControlled
      ? externalModalOpen
      : internalModalOpen;
    const setIsPlacementModalOpen = isExternallyControlled
      ? (open: boolean) => {
          if (!open && onClosePlacementModal) onClosePlacementModal();
        }
      : setInternalModalOpen;
    const [replaceModalState, setReplaceModalState] = useState<{
      isOpen: boolean;
      elementType: VideoTemplateElementType | null;
      isAdding: boolean;
    }>({
      isOpen: false,
      elementType: null,
      isAdding: false,
    });
    const [captionsPreviewRect, setCaptionsPreviewRect] =
      useState<DOMRect | null>(null);
    // Captions render live rather than from an image, so fit the preview to the
    // video's aspect ratio using the same box as the other element previews.
    const captionsPreviewSize = fitPreviewToAspectRatio(
      parseAspectRatio(videoCreator.getCurrentAspectRatio()) ?? 16 / 9,
      Math.min(ELEMENT_PREVIEW_MAX_HEIGHT_PX, viewportMaxPreviewHeight()),
    ) ?? { width: 0, height: 0 };

    if (!activeTemplateSettings) {
      return null;
    }

    const config = activeTemplate?.configuration;

    // Helper to get preview image from local replacements (when element doesn't exist in config)
    const getLocalPreviewImage = (
      elementType: VideoTemplateElementType,
    ): string | undefined => {
      const replacedId = getReplacedElementId(elementType);
      if (!replacedId) return undefined;

      if (elementType === 'logo') {
        // For logo, replacedId IS the URL directly
        return replacedId;
      }

      if (['introCard', 'endCard', 'nameCard', 'hook'].includes(elementType)) {
        const composition = getCompositionById(replacedId);
        return composition?.thumbnail?.url;
      }

      // Captions handled by CaptionsPreviewThumbnail component
      return undefined;
    };

    // Helper to check if a local replacement exists for an element type
    const hasLocalReplacement = (type: VideoTemplateElementType) =>
      !!getReplacedElementId(type);

    // Helper to get element name by looking up the effective template from context
    const getElementName = (
      type: VideoTemplateElementType,
    ): string | undefined => {
      if (type === 'logo') return undefined;

      const replacedId = getReplacedElementId(type);

      if (type === 'captions') {
        const captionsId =
          replacedId || config?.captionsConfiguration?.captionsConfig?.id;
        if (!captionsId) return undefined;
        const template =
          videoCreator.textBrandingService?.brandingTemplates?.find(
            t => t.id === captionsId,
          );
        const title = template?.title;
        return typeof title === 'string'
          ? title
          : config?.captionsConfiguration?.captionsConfig?.title;
      }

      // For card types (introCard, endCard, nameCard)
      const originalId =
        type === 'introCard'
          ? config?.introCardConfiguration?.compositionTemplate?.id
          : type === 'endCard'
            ? config?.endCardConfiguration?.compositionTemplate?.id
            : config?.namecardConfiguration?.compositionTemplate?.id;

      const effectiveId = replacedId || originalId;
      if (!effectiveId) return undefined;

      const configLabel =
        type === 'introCard'
          ? config?.introCardConfiguration?.compositionTemplate?.label
          : type === 'endCard'
            ? config?.endCardConfiguration?.compositionTemplate?.label
            : type === 'hook'
              ? config?.hookConfiguration?.compositionTemplate?.label
              : config?.namecardConfiguration?.compositionTemplate?.label;

      return getCompositionById(effectiveId)?.label ?? configLabel;
    };

    // Build list of configuration items that are defined
    const configItems: Array<{
      type: VideoTemplateElementType;
      label: string;
      elementEnabled: boolean;
      itemDisabled?: boolean;
      tooltip?: string;
      image?: string;
      exists?: boolean;
      name?: string;
    }> = [];
    const hasSpeaker = speakers.length > 0;
    const activeTemplateElements = activeTemplateSettings.videoTemplateElements;

    configItems.push({
      label: 'Logo',
      type: 'logo',
      elementEnabled: isEnabledOptimistic('logo'),
      image:
        getLocalPreviewImage('logo') || config?.logoConfiguration?.logo?.url,
      exists:
        (!!config?.logoConfiguration && !!config?.logoConfiguration.logo) ||
        hasLocalReplacement('logo'),
    });

    configItems.push({
      label: 'Captions',
      elementEnabled: isEnabledOptimistic('captions'),
      type: 'captions',
      exists:
        (!!config?.captionsConfiguration &&
          !!config?.captionsConfiguration.captionsConfig) ||
        hasLocalReplacement('captions'),
      name: getElementName('captions'),
    });

    configItems.push({
      label: 'Intro Card',
      type: 'introCard',
      elementEnabled: isEnabledOptimistic('introCard'),
      image: isUploadedMediaCardId(getReplacedElementId('introCard'))
        ? getLocalPreviewImage('introCard')
        : getLocalPreviewImage('introCard') ||
          config?.introCardConfiguration?.compositionTemplate?.thumbnail?.url,
      exists:
        (!!config?.introCardConfiguration &&
          !!config?.introCardConfiguration.compositionTemplate) ||
        hasLocalReplacement('introCard'),
      name: getElementName('introCard'),
    });

    configItems.push({
      label: 'Namecard',
      elementEnabled: isEnabledOptimistic('nameCard'),
      type: 'nameCard',
      itemDisabled: !hasSpeaker,
      tooltip: !hasSpeaker ? 'Add a speaker to enable namecard' : undefined,
      image:
        getLocalPreviewImage('nameCard') ||
        config?.namecardConfiguration?.compositionTemplate?.thumbnail?.url,
      exists:
        (!!config?.namecardConfiguration &&
          !!config?.namecardConfiguration.compositionTemplate) ||
        hasLocalReplacement('nameCard'),
      name: getElementName('nameCard'),
    });

    configItems.push({
      label: 'End Card',
      type: 'endCard',
      elementEnabled: isEnabledOptimistic('endCard'),
      image: isUploadedMediaCardId(getReplacedElementId('endCard'))
        ? getLocalPreviewImage('endCard')
        : getLocalPreviewImage('endCard') ||
          config?.endCardConfiguration?.compositionTemplate?.thumbnail?.url,
      exists:
        (!!config?.endCardConfiguration &&
          !!config?.endCardConfiguration.compositionTemplate) ||
        hasLocalReplacement('endCard'),
      name: getElementName('endCard'),
    });

    // PLAT-11034 — last in the list: the existing order is one people have
    // learned, and the hook is a new selection at the bottom rather than a
    // re-shuffle of the four above it.
    configItems.push({
      label: 'Hook',
      type: 'hook',
      elementEnabled: isEnabledOptimistic('hook'),
      image:
        getLocalPreviewImage('hook') ||
        config?.hookConfiguration?.compositionTemplate?.thumbnail?.url,
      exists:
        (!!config?.hookConfiguration &&
          !!config?.hookConfiguration.compositionTemplate) ||
        hasLocalReplacement('hook'),
      name: getElementName('hook'),
    });

    const handleToggle = (
      elementType: VideoTemplateElementType,
      disabled?: boolean,
    ) => {
      if (disabled) return;
      queueToggle(elementType);
    };

    const handleReplaceConfirm = (newTemplateId: string) => {
      if (replaceModalState.elementType) {
        replaceTemplateElement(replaceModalState.elementType, newTemplateId);
      }
    };

    const handleReplace = (
      elementType: VideoTemplateElementType,
      isAdding: boolean,
    ) => {
      if (elementType === 'logo') {
        // Fall back to URL modal only if videoCreator is completely unavailable
        if (!videoCreator) {
          return;
        }

        const elementId = activeTemplateElements.find(
          e => e.type === 'logo',
        )?.id;

        // Try to get the real ElementState from the renderer if available
        // In clip mode, renderer may not be available - use a mock element
        // The actual replacement is handled by onImageSourceChange callback
        const element = elementId
          ? editor.timeline.getElement(elementId)
          : null;
        const photoReplaceElementState = element
          ? mapToElementState(element.toJSON())
          : ({
              source: { type: 'image', id: elementId || 'logo-element' },
            } as any);

        videoCreator.openPhotoElementReplacementModal = {
          element: photoReplaceElementState,
          isLogoControl: true,
          isVideoControl: false,
          onImageSourceChange: (url: string, uploadId?: string) =>
            replaceTemplateElement('logo', url, uploadId),
          callbackReplacesElement: true,
        };
        return;
      }
      if (elementType === 'captions') {
        // The karaoke branding list is only built by the captions sidebar, so
        // without this the modal lists whatever happens to be there — on a fresh
        // load, just the records other video templates reference.
        syncKaraokeBrandingTemplates();
      }

      setReplaceModalState({
        isOpen: true,
        elementType,
        isAdding,
      });
    };

    const handleCloseModal = () => {
      setReplaceModalState({
        isOpen: false,
        elementType: null,
        isAdding: false,
      });
    };

    return (
      <Container data-testid="video-template-settings">
        <VideoTemplateSettingsContainer disabled={disabled}>
          <ToggleList>
            {configItems.map(item => (
              <ToggleItem
                key={item.type}
                data-tooltip-id="element-tooltip"
                data-tooltip-content={item.tooltip}
                disabled={item.itemDisabled}
              >
                <Tooltip
                  id="element-tooltip"
                  style={{ ...COMPACT_TOOLTIP_STYLE, textAlign: 'center' }}
                  place="top"
                  positionStrategy="fixed"
                />
                <ToggleItemLeft>
                  <Switch
                    label={item.label}
                    labelColor={
                      item.itemDisabled
                        ? 'var(--text-muted)'
                        : 'var(--text-secondary)'
                    }
                    checked={!!item.elementEnabled && !item.itemDisabled}
                    onClick={() => handleToggle(item.type, item.itemDisabled)}
                    disabled={item.itemDisabled || !item.exists}
                    checkedColor="var(--accent)"
                    labelSize="var(--text-sm)"
                    small
                  />
                </ToggleItemLeft>
                {item.name && isClipMode && (
                  <ImageLabel>{item.name}</ImageLabel>
                )}
                <ToggleItemRight $compact={compact}>
                  {item.image ? (
                    <EnlargedImageRenderProp
                      src={item.image}
                      smallPhotoHeight={65}
                      direction="left"
                      isLogo={item.type === 'logo'}
                      darkBackground
                      enlargedPhotoHeight={ELEMENT_PREVIEW_MAX_HEIGHT_PX}
                    >
                      {({
                        enlargedState,
                        setEnlargedState,
                        renderEnlargedImage,
                      }) => (
                        <>
                          <ImageWrapper
                            hasImage
                            onClick={() => {
                              if (item.itemDisabled) return;
                              handleReplace(item.type, !item.exists);
                            }}
                            onMouseEnter={e => {
                              setEnlargedState(
                                e.currentTarget.getBoundingClientRect(),
                              );
                            }}
                            onMouseLeave={() => {
                              setEnlargedState(null);
                            }}
                          >
                            <img src={item.image} alt={item.label} />
                            <ReplaceButton
                              disabled={item.itemDisabled}
                              alwaysShow={!item.exists}
                            >
                              {item.exists ? 'Replace' : 'Add'}
                            </ReplaceButton>
                            <ImageOverlay />
                          </ImageWrapper>
                          {enlargedState &&
                            !item.itemDisabled &&
                            renderEnlargedImage()}
                        </>
                      )}
                    </EnlargedImageRenderProp>
                  ) : (
                    <CaptionsPreviewContainer
                      onMouseEnter={e =>
                        item.type === 'captions' &&
                        item.exists &&
                        setCaptionsPreviewRect(
                          e.currentTarget.getBoundingClientRect(),
                        )
                      }
                      onMouseLeave={() => setCaptionsPreviewRect(null)}
                    >
                      <ImageWrapper
                        hasImage={item.type === 'captions' && item.exists}
                        onClick={() => {
                          if (item.itemDisabled) return;
                          handleReplace(item.type, !item.exists);
                        }}
                      >
                        {item.type === 'captions' ? (
                          <CaptionsPreviewThumbnail
                            videoCreator={videoCreator}
                            hideTextPreview={!item.exists}
                            activeConfigId={
                              getReplacedElementId('captions') ||
                              activeTemplate?.configuration
                                ?.captionsConfiguration?.captionsConfig?.id
                            }
                          />
                        ) : null}
                        <ReplaceButton
                          disabled={item.itemDisabled}
                          alwaysShow={!item.exists}
                        >
                          {item.exists ? 'Replace' : 'Add'}
                        </ReplaceButton>
                        <ImageOverlay />
                      </ImageWrapper>
                      {item.type === 'captions' &&
                        item.exists &&
                        captionsPreviewRect && (
                          <EnlargedPreviewCard
                            anchorRect={captionsPreviewRect}
                            anchorHeight={captionsPreviewRect.height}
                            width={captionsPreviewSize.width}
                            height={captionsPreviewSize.height}
                            direction="left"
                            darkBackground
                          >
                            <CaptionsPreviewThumbnail
                              videoCreator={videoCreator}
                              activeConfigId={
                                getReplacedElementId('captions') ||
                                activeTemplate?.configuration
                                  ?.captionsConfiguration?.captionsConfig?.id
                              }
                            />
                          </EnlargedPreviewCard>
                        )}
                    </CaptionsPreviewContainer>
                  )}
                </ToggleItemRight>
              </ToggleItem>
            ))}
          </ToggleList>
          {!isExternallyControlled && (
            <GearRow>
              <GearButton
                onClick={() => setIsPlacementModalOpen(true)}
                disabled={disabled}
                aria-label="Update template placements"
              >
                <GearIcon width="16" height="16" color="#848484" />
              </GearButton>
            </GearRow>
          )}
        </VideoTemplateSettingsContainer>

        <UpdateTemplatePlacementModal
          isOpen={isPlacementModalOpen}
          onClose={() => setIsPlacementModalOpen(false)}
          onUpdate={
            hasAccessToEditAll || hasAccessToEditThisTemplate
              ? updateTemplateConfig
              : undefined
          }
          onSaveAsCopy={saveTemplateAsCopy}
          canEditLockPosition={canEditLockPosition}
          templateName={activeTemplate?.label}
          aspectRatio={videoCreator.getCurrentAspectRatio()}
          options={configItems.map(
            (item): ElementOption => ({
              type: item.type,
              label: item.label,
              exists: !!item.exists,
              configEnabled:
                getConfigEnabled(item.type, config) ?? item.elementEnabled,
              configLockPosition: getConfigLockPosition(item.type, config),
              hasPlacement:
                item.type === 'logo' ||
                item.type === 'captions' ||
                item.type === 'nameCard' ||
                // PLAT-11034 — the hook is a positionable overlay, like the
                // nametag: where the creator leaves it can be saved back.
                item.type === 'hook',
              activeInEditor: item.elementEnabled,
              // An uploaded media card stays this video's own (PLAT-11732):
              // it has no record a template could point at.
              hasReplacement:
                hasLocalReplacement(item.type) &&
                !isUploadedMediaCardId(getReplacedElementId(item.type)),
              // Only compute deviation while the modal is open to avoid running
              // the placement collectors (and their logging) on every render.
              placementDiffersFromTemplate:
                isPlacementModalOpen && item.elementEnabled
                  ? getPlacementDeviation(item.type)
                  : false,
            }),
          )}
        />

        {replaceModalState.isOpen && replaceModalState.elementType && (
          <>
            {replaceModalState.elementType === 'captions' ? (
              <ReplaceCaptionsModal
                isOpen={replaceModalState.isOpen}
                onClose={handleCloseModal}
                onReplace={handleReplaceConfirm}
                isAdding={replaceModalState.isAdding}
                videoCreator={videoCreator}
                activeConfigId={
                  getReplacedElementId('captions') ||
                  activeTemplate?.configuration?.captionsConfiguration
                    ?.captionsConfig?.id
                }
              />
            ) : replaceModalState.elementType !== 'logo' ? (
              <ReplaceElementModal
                isOpen={replaceModalState.isOpen}
                onClose={handleCloseModal}
                elementType={replaceModalState.elementType}
                onReplace={handleReplaceConfirm}
                isAdding={replaceModalState.isAdding}
              />
            ) : null}
          </>
        )}
      </Container>
    );
  },
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const VideoTemplateSettingsContainer = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 8px 0px;
  gap: 8px;

  ${props =>
    props.disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
      cursor: not-allowed;
    `}
`;

const ToggleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ToggleItem = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'default')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
`;

const ToggleItemLeft = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const ToggleItemRight = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex: ${props => (props.$compact ? 'none' : '2')};
  justify-content: flex-end;
`;

const ImageLabel = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
  color: var(--text-muted);
  text-align: left;
  white-space: normal;
  line-height: 1.3;
  flex: 1;

  @media (max-width: 1150px) {
    display: none;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: inherit;
  opacity: 0;
  transition: opacity var(--transition-normal) ease-out;
`;

const ReplaceButton = styled.button<{ alwaysShow?: boolean }>`
  outline: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  padding: 5px 10px;
  position: absolute;
  color: white;
  font-family: var(--font-primary);
  font-weight: var(--font-weight-medium);
  font-size: var(--text-xs);
  text-align: center;
  background-color: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  cursor: pointer;
  white-space: nowrap;
  opacity: ${props => (props.alwaysShow ? 1 : 0)};
  transition: all var(--transition-normal) ease-out;

  &:hover {
    background-color: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ImageWrapper = styled.div<{ hasImage?: boolean }>`
  width: 90px;
  height: 58px;
  flex-shrink: 0;
  border-radius: var(--radius);
  background-color: ${props =>
    props.hasImage ? 'var(--popover)' : 'var(--background)'};
  position: relative;
  border: 1px solid var(--border);
  cursor: pointer;
  overflow: hidden;

  &:hover {
    outline: 2px solid var(--primary);
    outline-offset: -2px;
  }

  &:hover ${ImageOverlay} {
    opacity: 1;
  }

  &:hover ${ReplaceButton} {
    opacity: 1;
  }

  &:hover ${ReplaceButton}:disabled {
    opacity: 0.5;
  }

  & img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 6px;
    padding: 4px;
    box-sizing: border-box;
  }
`;

const CaptionsPreviewContainer = styled.div`
  position: relative;
`;

const GearRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
`;

const GearButton = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
